import React, { useState } from "react";
import { Sop } from "../types/sop";
import { exportSop } from "../services/api";

interface Props {
  sop: Sop;
  onApprove: () => void;
  onReject: () => void;
}

export function SopCard({ sop, onApprove, onReject }: Props) {
  const [exporting, setExporting] = useState(false);
  const statusColor = { transcribing: "#2196f3", pending_review: "#ff9800", approved: "#4caf50", rejected: "#f44336", error: "#c62828" }[sop.status] ?? "#999";

  const handleExport = async () => {
    setExporting(true);
    try {
      const { download_url } = await exportSop(sop.sop_id, sop.station_id);
      window.open(download_url, "_blank");
    } catch (e) {
      console.error(e);
    }
    setExporting(false);
  };

  return (
    <div style={{ border: "1px solid #ddd", borderRadius: 8, padding: 20, marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3 style={{ margin: 0 }}>{sop.title ?? "Untitled SOP"}</h3>
        <span style={{ background: statusColor, color: "#fff", padding: "4px 12px", borderRadius: 12, fontSize: 13 }}>
          {sop.status}
        </span>
      </div>

      <p style={{ color: "#666", margin: "8px 0" }}>
        {sop.process_area} · {sop.target_role} · {sop.station_id}
      </p>

      {sop.purpose && <p><strong>Purpose:</strong> {sop.purpose}</p>}

      {sop.steps?.length > 0 && (
        <>
          <h4>Steps</h4>
          <ol style={{ paddingLeft: 20 }}>
            {sop.steps.map((s) => (
              <li key={s.step_number} style={{ marginBottom: 8 }}>
                <strong>{s.action}</strong>
                {s.detail && <span style={{ color: "#555" }}> — {s.detail}</span>}
                {s.safety_callout && <div style={{ color: "#d32f2f", fontSize: 13 }}>⚠️ {s.safety_callout}</div>}
                {s.tip && <div style={{ color: "#1976d2", fontSize: 13 }}>💡 {s.tip}</div>}
              </li>
            ))}
          </ol>
        </>
      )}

      {sop.common_mistakes?.length > 0 && (
        <>
          <h4>Common Mistakes</h4>
          <ul>
            {sop.common_mistakes.map((m, i) => (
              <li key={i}>❌ {m.mistake} → ✅ {m.correction}</li>
            ))}
          </ul>
        </>
      )}

      {sop.quick_reference && (
        <div style={{ background: "#f5f5f5", padding: 12, borderRadius: 6, marginTop: 12 }}>
          <strong>Quick Reference Card</strong>
          <pre style={{ whiteSpace: "pre-wrap", margin: "8px 0 0", fontSize: 13 }}>{sop.quick_reference}</pre>
        </div>
      )}

      <div style={{ marginTop: 16, display: "flex", gap: 12 }}>
        {sop.status === "pending_review" && (
          <>
            <button onClick={onApprove}
              style={{ flex: 1, padding: 10, background: "#4caf50", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer" }}>
              ✅ Approve
            </button>
            <button onClick={onReject}
              style={{ flex: 1, padding: 10, background: "#f44336", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer" }}>
              ❌ Reject
            </button>
          </>
        )}
        {sop.status === "approved" && (
          <button onClick={handleExport} disabled={exporting}
            style={{ padding: 10, background: "#1976d2", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer" }}>
            {exporting ? "Generating..." : "🖨 Export / Print"}
          </button>
        )}
      </div>
    </div>
  );
}
